import { TextareaHTMLAttributes, forwardRef, useId } from "react";
import clsx from "clsx";

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  /** Shows success state styling */
  success?: boolean;
  /** Character count (current/max) */
  characterCount?: { current: number; max: number };
  /** Auto-resize height based on content */
  autoResize?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      className,
      success,
      characterCount,
      autoResize = false,
      id: providedId,
      "aria-describedby": providedAriaDescribedBy,
      onChange,
      ...props
    },
    ref
  ) => {
    // Generate unique IDs for accessibility
    const generatedId = useId();
    const textareaId = providedId || generatedId;
    const errorId = `${textareaId}-error`;
    const helperId = `${textareaId}-helper`;
    const countId = `${textareaId}-count`;

    // Build aria-describedby based on what's visible
    const ariaDescribedByParts: string[] = [];
    if (providedAriaDescribedBy) ariaDescribedByParts.push(providedAriaDescribedBy);
    if (error) ariaDescribedByParts.push(errorId);
    if (helperText && !error) ariaDescribedByParts.push(helperId);
    if (characterCount) ariaDescribedByParts.push(countId);

    const ariaDescribedBy =
      ariaDescribedByParts.length > 0 ? ariaDescribedByParts.join(" ") : undefined;

    // Determine border styling based on state
    const getBorderClasses = () => {
      if (error) return "border-red-500 focus:ring-red-500";
      if (success) return "border-green-500 focus:ring-green-500";
      return "border-neutral-300 dark:border-neutral-600 focus:ring-primary-500";
    };

    // Handle auto-resize
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (autoResize) {
        e.target.style.height = "auto";
        e.target.style.height = `${e.target.scrollHeight}px`;
      }
      onChange?.(e);
    };

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1"
          >
            {label}
            {props.required && (
              <span className="text-red-500 ml-1" aria-hidden="true">
                *
              </span>
            )}
            {props.required && <span className="sr-only">(required)</span>}
          </label>
        )}
        <div className="relative">
          <textarea
            ref={ref}
            id={textareaId}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={ariaDescribedBy}
            onChange={handleChange}
            className={clsx(
              "w-full px-4 py-2 border rounded-lg transition-all",
              "focus:outline-none focus:ring-2 focus:border-transparent",
              "bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100",
              "placeholder:text-neutral-400 dark:placeholder:text-neutral-500",
              "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-neutral-100 dark:disabled:bg-neutral-900",
              autoResize ? "resize-none overflow-hidden" : "resize-y",
              // Minimum height for touch targets
              "min-h-[88px]",
              getBorderClasses(),
              className
            )}
            {...props}
          />
          {/* Success indicator */}
          {success && (
            <div className="absolute top-3 right-3 text-green-500" aria-hidden="true">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Bottom row: error/helper on left, character count on right */}
        <div className="flex justify-between items-start mt-1 gap-2">
          <div className="flex-1">
            {/* Error message */}
            {error && (
              <p
                id={errorId}
                className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1"
                role="alert"
              >
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </p>
            )}

            {/* Helper text */}
            {helperText && !error && (
              <p id={helperId} className="text-sm text-neutral-500 dark:text-neutral-400">
                {helperText}
              </p>
            )}
          </div>

          {/* Character count */}
          {characterCount && (
            <p
              id={countId}
              className={clsx(
                "text-xs flex-shrink-0",
                characterCount.current > characterCount.max ? "text-red-500" : "text-neutral-400"
              )}
              aria-live="polite"
            >
              {characterCount.current}/{characterCount.max}
            </p>
          )}
        </div>
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export default Textarea;
